import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["dropdown", "button", "flag", "label", "arrow"]
  static values = {
    endpoint: { type: String, default: "/settings/language" },
    successEn: String,
    successNl: String,
    error: String
  }

  connect() {
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.closeOnClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnClickOutside)
  }

  toggle(event) {
    event.stopPropagation()
    this.dropdownTarget.classList.toggle("hidden")
    this.arrowTarget.classList.toggle("rotate-180")
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
    this.arrowTarget.classList.remove("rotate-180")
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  async select(event) {
    const language = event.currentTarget.dataset.language
    const currentLanguage = this.flagTarget.textContent.includes("🇳🇱") ? "nl" : "en"

    if (language === currentLanguage) {
      this.close()
      return
    }

    this.buttonTarget.classList.add("opacity-50", "pointer-events-none")

    try {
      const response = await fetch(this.endpointValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ language: language })
      })

      if (response.ok) {
        this.updateUI(language)
        this.showSuccessNotification(language)
        // Reload the page with Turbo to update all translated content
        setTimeout(() => {
          Turbo.visit(window.location.href, { action: "replace" })
        }, 500)
      } else {
        this.showErrorNotification()
      }
    } catch (error) {
      console.error("Failed to update language:", error)
      this.showErrorNotification()
    } finally {
      this.buttonTarget.classList.remove("opacity-50", "pointer-events-none")
      this.close()
    }
  }

  updateUI(language) {
    const isNL = language === "nl"
    this.flagTarget.textContent = isNL ? "🇳🇱" : "🇬🇧"
    this.labelTarget.textContent = isNL ? "NL" : "EN"

    const buttons = this.dropdownTarget.querySelectorAll("button")
    buttons.forEach(btn => {
      const isSelected = btn.dataset.language === language
      btn.classList.toggle("text-indigo-600", isSelected)
      btn.classList.toggle("bg-indigo-50", isSelected)
      btn.classList.toggle("text-gray-700", !isSelected)

      const checkmark = btn.querySelector("svg")
      if (checkmark) {
        checkmark.remove()
      }
      if (isSelected) {
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        svg.setAttribute("class", "w-4 h-4 ml-auto text-indigo-600")
        svg.setAttribute("fill", "currentColor")
        svg.setAttribute("viewBox", "0 0 20 20")
        svg.innerHTML = '<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>'
        btn.appendChild(svg)
      }
    })
  }

  showSuccessNotification(language) {
    const message = language === "nl"
      ? this.successNlValue
      : this.successEnValue

    this.showToast(message, "success")
  }

  showErrorNotification() {
    this.showToast(this.errorValue, "error")
  }

  showToast(message, type) {
    const toast = document.createElement("div")
    toast.className = `fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 transition-all transform translate-y-0 opacity-100 ${
      type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
    }`
    toast.textContent = message

    document.body.appendChild(toast)

    setTimeout(() => {
      toast.classList.add("opacity-0", "translate-y-2")
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }
}
